import React, { useCallback } from 'react';

import { Command, MousePointerClick } from 'lucide-react';

import EntityPopover from '../shared/EntityPopover';

const PopoverContent = ({ reaction, backgroundImageUrl, fallbackGlyph, truncatedShortcode, onRemoveClick }) => {
  return (
    <div className="flex w-40 flex-col items-center gap-2 px-3 pt-3 pb-5 font-medium">
      <div
        className="h-14 w-14 bg-contain bg-center bg-no-repeat"
        style={backgroundImageUrl ? { backgroundImage: `url(${backgroundImageUrl})` } : undefined}
      >
        {!backgroundImageUrl && <span className="text-5xl leading-none">{fallbackGlyph}</span>}
      </div>
      {/* Type matches the shared Tooltip helper spec: 13px/20 medium. */}
      <div className="flex w-full min-w-0 flex-col items-center gap-1.5 p-2">
        <div className="w-full min-w-0 text-center text-white">
          <span className="block w-full truncate text-[13px] leading-5 font-medium">{truncatedShortcode}</span>
        </div>
        {reaction.isOwnReaction ? (
          <div className="flex items-center gap-1.5 text-[13px] leading-5 font-medium whitespace-nowrap text-gray-400">
            <span className="flex items-center gap-1 text-white">
              <Command className="size-3.5" strokeWidth={2} absoluteStrokeWidth />
              <MousePointerClick className="size-3.5" strokeWidth={2} absoluteStrokeWidth />
            </span>
            or
            <button
              type="button"
              onClick={onRemoveClick}
              className="cursor-pointer rounded-full bg-white/10 px-2 py-0.5 text-[13px] leading-5 font-medium text-red-400 hover:bg-white/15"
            >
              Remove
            </button>
          </div>
        ) : (
          <span className="block w-full min-w-0 truncate text-center text-[13px] leading-5 font-medium text-gray-400">
            {reaction.profile.full_name}
          </span>
        )}
      </div>
    </div>
  );
};

// Reaction objects arrive with `emoji` and `profile` snapshots pre-attached by
// the message feed's renderer-row enrichment so no lookups are required here.
const TapbackReaction = ({ reaction, interactive, onReactionClick, canCreateReactions = true }) => {
  const hasTooltipData = reaction.profile && reaction.emoji;

  const emojiShortcode = reaction.emoji?.shortcode;
  const normalizedShortcode = emojiShortcode ? `:${emojiShortcode}:` : null;
  const truncatedShortcode =
    normalizedShortcode && normalizedShortcode.length > 17 ? `${normalizedShortcode.slice(0, 16)}…` : normalizedShortcode;
  const backgroundImageUrl = reaction.emoji?.public_url;
  const fallbackGlyph = reaction.emoji?.native || (emojiShortcode ? `:${emojiShortcode}:` : '🙂');

  const handleClick = useCallback(
    (event) => {
      if (!interactive || !canCreateReactions) return;
      onReactionClick(reaction, event);
    },
    [interactive, canCreateReactions, onReactionClick, reaction],
  );

  const handleRemoveClick = useCallback(
    (event) => {
      if (!interactive || !canCreateReactions) return;
      // Explicit remove: a plain click on an own reaction stacks another copy now,
      // so the popover's Remove must say what it means.
      onReactionClick(reaction, event, { forceRemove: true });
    },
    [interactive, canCreateReactions, onReactionClick, reaction],
  );

  const popoverContent = hasTooltipData ? (
    <PopoverContent
      reaction={reaction}
      backgroundImageUrl={backgroundImageUrl}
      fallbackGlyph={fallbackGlyph}
      truncatedShortcode={truncatedShortcode}
      onRemoveClick={handleRemoveClick}
    />
  ) : null;

  return (
    <div
      className={`relative inline-flex size-9 origin-center items-center justify-center ${interactive && canCreateReactions ? 'cursor-pointer' : ''}`}
      data-app-region={interactive ? 'none' : 'drag'}
      data-reaction-emoji-id={reaction.emoji_id}
      data-reaction-id={reaction.id}
    >
      <EntityPopover content={popoverContent} interactive={interactive}>
        <div
          onClick={handleClick}
          className="size-8 bg-contain bg-center bg-no-repeat transition-transform hover:[transform:translate(-1.6px,-1.6px)_rotate(15deg)]"
          style={backgroundImageUrl ? { backgroundImage: `url(${backgroundImageUrl})` } : undefined}
        >
          {!backgroundImageUrl && <span className="text-lg leading-none">{fallbackGlyph}</span>}
        </div>
      </EntityPopover>
    </div>
  );
};

export default TapbackReaction;
